import { useStudioStore } from '../../store/studio-store'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { formatStudioProjectPlanSummary } from '@/features/project/lib/studio-project-plan'
import { useStudioConversationSurfaceLabel } from './components/artifact-mode-switcher'
import { useStudioLocale } from '@/i18n'
import { COOGO_IDE_CHROME_COPY } from './coogo-ide-chrome-copy'

/**
 * PR5 visual polish: slim h-8 strip with compact project context + status.
 *
 * Block count slot is a placeholder ("—") in PR5 — wired to real canvas state in
 * a later sub-project B PR. Status indicator uses DS v4 `--success` token.
 *
 * Phase 531 (RESP-02): the bar is a Radix Collapsible bound to the persisted
 * `bottomBarExpanded` flag (cogo-studio-ui-v1), toggled via the shortcut bus (⌘,).
 * The always-visible h-8 strip doubles as the collapse trigger so the project
 * name + status stay reachable in both states; an extra detail row reveals when
 * expanded.
 */
export function BottomBar() {
  const { locale } = useStudioLocale()
  const copy = COOGO_IDE_CHROME_COPY[locale]
  const bottomBarExpanded = useStudioStore((s) => s.bottomBarExpanded)
  const toggleBottomBar = useStudioStore((s) => s.toggleBottomBar)
  const isSaving = useStudioStore((s) => s.isSaving)
  const isDirty = useStudioStore((s) => s.isDirty)
  const saveError = useStudioStore((s) => s.saveError)
  const saveConflict = useStudioStore((s) => s.saveConflict)
  const projectMeta = useStudioStore((s) => s.projectMeta)
  const projectPlan = useStudioStore((s) => s.projectPlan)
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const developer = useStudioStore((s) => s.studioPersona === 'developer')
  const activeRailItem = useStudioStore((s) => s.activeRailItem)
  const surfaceLabel = useStudioConversationSurfaceLabel(activeRailItem)

  // F11 — an unresolved save conflict outranks every other state: the edit
  // was halted (not retried), so neither Ready nor Save failed is truthful.
  const status = saveConflict
    ? {
        label: copy.conflict,
        tone: 'bg-destructive',
        aria: copy.statusConflict,
      }
    : saveError
      ? {
          label: copy.saveFailed,
          tone: 'bg-destructive',
          aria: copy.statusSaveFailed,
        }
      : isSaving
        ? {
            label: copy.saving,
            tone: 'bg-muted-foreground/60',
            aria: copy.statusSaving,
          }
        : isDirty
          ? {
              label: copy.unsaved,
              tone: 'bg-muted-foreground/60',
              aria: copy.statusUnsaved,
            }
          : {
              label: copy.ready,
              tone: 'bg-foreground',
              aria: copy.statusReady,
            }
  const statusDetail = saveConflict ? copy.statusConflict : saveError
  const projectPlanSummary = currentProjectId
    ? formatStudioProjectPlanSummary(
        projectPlan ?? projectMeta?.studioProjectPlan,
        projectMeta?.app_target,
        locale,
      )
    : null;

  if (!developer) return null;

  return (
    <Collapsible
      open={bottomBarExpanded}
      onOpenChange={() => toggleBottomBar()}
      className="shrink-0"
    >
      <CollapsibleTrigger
        data-testid="bottom-bar-trigger"
        aria-label={copy.toggleStatusDetails}
        className="flex h-8 min-w-0 w-full items-center gap-3 overflow-hidden border-t border-border bg-card px-3 text-xs text-muted-foreground"
      >
        <span className="shrink-0 font-semibold text-foreground">Coogo</span>
        <span
          data-testid="bottombar-project-name"
          className="shrink-0 text-muted-foreground"
          title={copy.currentWorkspaceMode}
        >
          {copy.connected}
        </span>
        <span className="shrink-0 opacity-60" aria-hidden>
          •
        </span>
        <span className="shrink-0">{copy.conversation(surfaceLabel)}</span>
        {projectPlanSummary && (
          <>
            <span className="shrink-0 opacity-60" aria-hidden>
              •
            </span>
            <span
              data-testid="bottombar-project-plan"
              className="hidden max-w-[180px] shrink-0 truncate text-muted-foreground sm:inline"
              title={projectPlanSummary}
            >
              {projectPlanSummary}
            </span>
          </>
        )}
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <span
            className={`w-1.5 h-1.5 rounded-full ${status.tone}`}
            aria-label={status.aria}
          />
          <span>{status.label}</span>
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent
        data-testid="bottom-bar-content"
        className="min-w-0 border-t border-border bg-card px-3 pb-2 text-[11px] text-muted-foreground/70"
      >
        <span className="studio-ellipsis-block" title={statusDetail ?? copy.currentWorkspaceMode}>
          {statusDetail ?? (projectPlanSummary ? `${copy.ready} · ${projectPlanSummary}` : copy.ready)}
        </span>
      </CollapsibleContent>
    </Collapsible>
  );
}
